import React, { useMemo, useState } from 'react';
import { Box } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import OptionsFilter from '../../atoms/OptionsFilter';
import {
  getSearchEntryError,
  isBooleanSearch,
  MAX_KEYWORD_LENGTH,
  SEARCH_ENTRY_ERRORS,
} from '../../../helpers/booleanValidator';
import { ApiFacet } from './apiFacet';

export const JobTitle = ({ filters, setFilters, sharedList, showListFilters }) => {
  const [error, setError] = useState(null);
  const selected = useMemo(
    () =>
      filters.jobTitles.map((item) => ({
        ...item,
        isEditable: isBooleanSearch(item.value),
      })),
    [filters.jobTitles],
  );

  // Entries can arrive already selected from a saved or shared filter, which
  // never go through the input handlers. Flag them so the search is not
  // silently rejected server-side; the saved value itself is left alone.
  const oversizedSelected = useMemo(
    () => selected.filter((item) => getSearchEntryError((item.value || '').trim()) === SEARCH_ENTRY_ERRORS.tooLong),
    [selected],
  );

  // Judge the trimmed value — that is what gets committed as a tag.
  const validateJobTitleValue = (value) => {
    const entryError = getSearchEntryError((value || '').trim());
    setError(entryError);

    return !entryError;
  };

  const getErrorMessage = () => {
    if (error === SEARCH_ENTRY_ERRORS.tooLong) {
      return <Box>Job title is too long (max {MAX_KEYWORD_LENGTH} characters).</Box>;
    }

    if (error === SEARCH_ENTRY_ERRORS.invalidBoolean) {
      return (
        <Box>
          Invalid boolean format.{' '}
          <Box
            color="inherit"
            as="a"
            href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </Box>
          .
        </Box>
      );
    }

    return null;
  };

  return (
    <ApiFacet
      name="job_titles"
      label="Job title"
      value={selected}
      onChange={(value) => {
        const booleanSearch = selected.find((item) => item.isEditable);
        const addedBooleanSearch = value.length > selected.length && isBooleanSearch(value[value.length - 1].value);

        setFilters({
          jobTitles:
            addedBooleanSearch && booleanSearch ? value.filter((item) => item.value !== booleanSearch.value) : value,
        });
      }}
      notFoundText="No contacts with job title"
      sharedList={sharedList}
      useSearchAsTag
      useShareListFilter={!showListFilters}
      placeholder="Select job title or press enter"
      isValidTagValue={validateJobTitleValue}
      isValidSearch={(value) => {
        const isValid = validateJobTitleValue(value);

        return isValid && !isBooleanSearch(value);
      }}
      titleMessage={
        <Box sx={{ fontSize: '12px', color: 'text.subtle' }}>
          Learn to use boolean searches{' '}
          <Box
            color="text.subtle"
            as="a"
            href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </Box>
          .
        </Box>
      }
      errorMessage={getErrorMessage()}
    >
      <>
        {oversizedSelected.length > 0 && (
          <Box sx={{ fontSize: '12px', color: 'red.500' }}>
            {oversizedSelected.length === 1
              ? '1 selected job title is over'
              : `${oversizedSelected.length} selected job titles are over`}{' '}
            {MAX_KEYWORD_LENGTH} characters and will be rejected. Remove or shorten it to run the search.
          </Box>
        )}
        <OptionsFilter
          placeholder="Current"
          selected={filters.jobTitlesIncluded}
          onSelect={(value) => setFilters({ jobTitlesIncluded: value })}
          options={[
            {
              label: 'Current',
              value: 'current',
            },
            {
              label: 'Recent',
              value: 'recent',
            },
            {
              label: 'Current and past',
              value: 'all',
            },
          ]}
        />
      </>
    </ApiFacet>
  );
};

JobTitle.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  sharedList: PropTypes.object,
  showListFilters: PropTypes.bool,
};

JobTitle.defaultProps = {
  sharedList: null,
  showListFilters: false,
};
